/**
 * useAutoRefresh — re-runs `fetcher` every `intervalMs` (with ±20 %
 * random jitter) while the document is visible. Pauses when the tab is
 * backgrounded (saves server load + mobile battery), instantly fires
 * once on resume so admins always see fresh data when they switch back.
 *
 * v2.4.9 (Feb 26, 2026) — added jitter so the ~800-device fleet's
 * lockstep polling (used to be exactly every 30 s / 7 s) no longer
 * arrives in a synchronized burst at the backend. This flattens the
 * peak QPS curve seen during the 08:30-10:00 IST morning window.
 *
 * Usage:
 *   const fetchData = useCallback(async () => { ... }, [deps]);
 *   useAutoRefresh(fetchData, 30000);
 */
import { useEffect, useRef } from 'react';

const DEFAULT_MS = 30_000;
const JITTER_RATIO = 0.2; // ±20 %

function jitteredInterval(baseMs) {
  // Uniform in [baseMs * (1 - r), baseMs * (1 + r)].
  const spread = baseMs * JITTER_RATIO;
  return Math.max(1_000, baseMs + (Math.random() * 2 - 1) * spread);
}

export default function useAutoRefresh(fetcher, intervalMs = DEFAULT_MS) {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let timer = null;

    const tick = () => {
      try { fetcherRef.current?.(); } catch {}
    };

    // Re-arm each cycle with a fresh random offset so successive polls
    // stay de-synchronised across devices instead of drifting back into
    // phase after one interval.
    const scheduleNext = () => {
      const next = jitteredInterval(intervalMs);
      timer = setTimeout(() => {
        tick();
        scheduleNext();
      }, next);
    };

    const start = () => {
      if (timer) return;
      scheduleNext();
    };
    const stop = () => {
      if (timer) { clearTimeout(timer); }
      timer = null;
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Refresh immediately on resume, then resume polling
        tick();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs]);
}
